import { useEffect, useMemo, useState } from "react";
import TabsWithBadge from "V2.0/common/tabsWithBadge";
import { GPTResponseModel, PromptSettings } from "../ModelSettings";
import { Button } from "V2.0/common/button";
import DemoComponent from "V2.0/components/DemoComponent";
import { useNavigate, useOutletContext } from "react-router-dom";
import {
  ChevronLeftIcon,
  Cog8ToothIcon,
  DocumentDuplicateIcon,
  FolderIcon,
  PhoneIcon,
  WrenchIcon,
} from "@heroicons/react/24/outline";

import { WhatsappAssistantTools } from "../AssistantTools/WhatsappAssistantTools";
import { KnowledgeBase } from "../RAG/KnowledgeBase/KnowledgeBase";
import { InstagramCommunicationBinding } from "../CommunicationBinding/InstagramCommunicationBinding";
import { FaInstagram } from "react-icons/fa6";
import AssistantsBackToHome from "./common/AssistantsBackToHome";
import AssistantName from "./common/AssistantName";
import AssistantIcon from "./common/AssistantIcon";
import { InstagramAssistantSettings } from "../AssistantSettings/InstagramAssistantSettings";
import AssistantId from "./common/AssistantId";
import GPTResponsePremiumModel from "../ModelSettings/GPTResponseModelCategorized/GPTResponsePremiumModel";
import { useAssistants } from "../Assistants";

enum TABS {
  PROMPT = "Prompt",
  TOOLS = "Tools",
  SETTINGS = "Settings",
  KNOWLEDGE = "Knowledge Base",
  PAGES = "Instagram Pages",
}

const tabsMap = {
  [TABS.PROMPT]: (props) => <PromptSettings {...props} />,
  [TABS.SETTINGS]: (props) => <InstagramAssistantSettings {...props} />,
  [TABS.TOOLS]: (props) => <WhatsappAssistantTools {...props} />, //Reusing whatsapp assistant tools for instagram
  [TABS.KNOWLEDGE]: (props) => <KnowledgeBase {...props} />,
  [TABS.PAGES]: (props) => <InstagramCommunicationBinding {...props} />,
};

const TAB_ICON = {
  [TABS.PROMPT]: DocumentDuplicateIcon,
  [TABS.KNOWLEDGE]: FolderIcon,
  [TABS.SETTINGS]: Cog8ToothIcon,
  [TABS.TOOLS]: WrenchIcon,
  [TABS.PAGES]: FaInstagram,
};

function useConfigureTabs(configurationSections) {
  const [selectedTab, setSelectedTab] = useState(null);

  const activeTab = selectedTab
    ? selectedTab
    : configurationSections.length > 0
      ? configurationSections[0]
      : null;

  const tabs = useMemo(() => {
    if (!configurationSections.length) return [];

    return configurationSections.reduce((acc, tab) => {
      const Icon = TAB_ICON[tab];
      return [
        ...acc,
        {
          name: tab,
          href: "#",
          current: tab === activeTab,
          icon: <Icon className="size-5" />,
        },
      ];
    }, []);
  }, [configurationSections, activeTab]);

  return {
    activeTab,
    tabs,
    setActiveTab: setSelectedTab,
  };
}

function InstagramAssistant(props) {
  const {
    workspaceId,
    name,
    assistantId,
    modelSettings,
    syncAssistant,
    assistantType,
    onDuplicate,
    company,
    locked,
    configurationSections,
  } = props;
  const { projects, activeProject } = useOutletContext<{
    projects: [];
    activeProject: object;
  }>();

  const { activeTab, tabs, setActiveTab } = useConfigureTabs(
    configurationSections,
  );
  const [chatOpen, setChatOpen] = useState<boolean>(false);
  const navigate = useNavigate();

    const { assistants, fetchAssistants } = useAssistants(workspaceId);

  useEffect(() => {
    if (workspaceId) {
      fetchAssistants();
    }
  }, [workspaceId]);

  return (
    <>
      <div className="flex flex-col h-full w-full">
        <div className="flex justify-between p-4 items-center border-b">
          <div className="flex gap-x-2">
            <AssistantsBackToHome />
            <AssistantIcon assistantType={assistantType} />
            <div className="flex flex-col gap-y-2">
              <AssistantName
                name={name}
                assistantId={assistantId}
                workspaceId={workspaceId}
                syncAssistant={syncAssistant}
                locked={locked}
              />
              <AssistantId assistantId={assistantId} />
            </div>
          </div>
          <div>
            <Button
              onClick={() => {
                setChatOpen(true);
              }}
            >
              Test Assistant
            </Button>
          </div>
        </div>
        <div className="flex justify-end p-4 border-b">
          <GPTResponsePremiumModel
            settings={modelSettings}
            syncAssistant={syncAssistant}
            workspaceId={workspaceId}
            assistantId={assistantId}
            showPremiumOptions={
              company?.thirdPartyIntegrationVisibility?.premiumModelVisibility
            }
            enablePreimumOptions={
              company?.thirdPartyIntegrationVisibility?.premiumModelsSelectable
            }
            locked={locked}
            audioDataResidency={props.voiceSettings?.audioDataResidency}
          />
        </div>

        <div className="border-b px-4">
          <TabsWithBadge
            tabs={tabs}
            className=""
            onTabChange={(e, { tab, index }) => setActiveTab(tab.name)}
          />
        </div>

        {activeTab ? (
          tabsMap[activeTab]({
            ...props,
            assistants,
          })
        ) : (
          <div className="flex-1 flex items-center justify-center m-4 mb-8 rounded-lg border">
            <p className="text-gray-500">No sections</p>
          </div>
        )}
      </div>
      {chatOpen && (
        <DemoComponent
          open={chatOpen}
          setOpen={setChatOpen}
          projects={projects}
          activeProject={activeProject}
          SidebarTitle="Chat with Assistant"
          assistantId={assistantId}
        />
      )}
    </>
  );
}

export { InstagramAssistant };
